import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import moment from 'moment';
import { css, cx } from '@bd/bdui-styles/emotion';
import { Image, SanitizeHtml } from '@bd/bdcore-utilities';
import { colors, typography } from '../../styles';

const styles = css`
  padding: 0 20px 90px;
  @media (min-width: 768px) {
    padding: 0 40px 110px;
  }
  @media (min-width: 992px) {
    padding: 0;
  }
  .PostBody_imageWrapper {
    margin-bottom: 28px;
    @media (min-width: 992px) {
      margin-bottom: 31px;
    }
  }
  .PostBody_image {
    display: block;
    width: 100%;
  }
  .PostBody_date {
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.125em;
    line-height: 1.375;
    text-transform: uppercase;
    color: #FFF;
    margin-bottom: 5px;
    @media (min-width: 768px) {
      font-size: 12px;
      margin-bottom: 7px;
    }
    @media (min-width: 992px) {
      font-size: 13px;
    }
  }
  .PostBody_title {
    font-family: ${typography.SerifHead};
    font-weight: bold;
    letter-spacing: 0.62px;
    font-style: italic;
    text-transform: capitalize;
    position: relative;
    @media (min-width: 768px) {
      font-size: 28px;
      padding-bottom: 22px;
    }
    @media (min-width: 992px) {
      font-size: 33px;
      padding-bottom: 26px;
    }
  }
  .PostBody_body {
    font-size: 14px;
    font-weight: normal;
    line-height: 2.142857;
    color: #FFF;
    > p:first-child {
      font-size: 16px;
      font-weight: 300;
      color: ${colors.color3Darkest};
      letter-spacing: 1.3px;
      font-style:italic;
    }
    > :last-child {
      margin-bottom: 0;
    }
    p, ul, ol {
      margin-bottom: 1.285714em;
    }
    a {
      font-weight: bold;
      @media (min-width: 768px) {
        font-weight: 800;
      }
    }
    ul, ol {
      margin-left: 1em;
      padding: 0;
      list-style: none;
      li {
        position: relative;
        padding-left: 24px;
        &:not(:last-child) {
          margin-bottom: 0.8em;
        }
        &::before {
          display: block;
          position: absolute;
          left: 0;
        }
      }
    }
    ul {
      li::before {
        content: '';
        width: 7px;
        height: 7px;
        top: 11px;
        background-color: ${colors.color1};
      }
    }
    ol {
      counter-reset: item;
      li {
        counter-increment: item;
        &::before {
          content: counter(item)'.';
          top: 9px;
          font-size: 1.1em;
          font-weight: 900;
          line-height: 0.75;
          color: ${colors.color1};
        }
      }
    }
    h3, h4, h5 {
      color: ${colors.color2};
      margin-bottom: 0.5em;
      margin-top: 1.25em;
    }
    h3, h4 {
      letter-spacing: 0.02em;
      margin-top: 1.25em;
    }
    h4, h5 {
      font-weight: bold;
    }
    h3 {
      font-size: 1.45em;
      font-weight: 600;
    }
    h4 {
      font-size: 1.3em;
    }
    h5 {
      font-size: 0.95em;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      margin-top: 1.625em;
    }
    img {
      max-width: 100%;
    }
    @media (min-width: 768px) {
      font-size: 15px;
      line-height: 2;
      > p:first-child {
        font-size: 18px;
      }
      ul, ol {
        margin-left: 1.5em;
        li {
          font-weight: normal;
          padding-left: 27px;
        }
      }
      ul li::before {
        width: 9px;
        height: 9px;
      }
    }
    @media (min-width: 992px) {
      font-size: 16px;
      > p:first-child {
        font-size: 20px;
      }
      ul li,
      ol li {
        padding-left: 30px;
      }
      a:hover {
        text-decoration: underline;
      }
    }
  }
`;

// since the blog WYSIWYG allows h2s, but SanitizeHtml will strip them out,
// transform them into h3s instead of letting them end up as plain text nodes
// inside .PostBody_body
const sanitizeHtmlOptions = {
  transformTags: {
    h2: 'h3',
  },
};

function PostBody({ blogpost, className }) {
  const blogImageSrc = get(blogpost, 'logo.contentUrl');
  const postDate = get(blogpost, 'date');
  const postBody = get(blogpost, 'htmlbody');
  return (
    <article className={cx(styles, className)}>
      {
        blogImageSrc &&
        <div className="PostBody_imageWrapper">
          <Image
            className="PostBody_image"
            src={blogImageSrc}
            alt={get(blogpost, 'logo.name', '')}
            width={800}
            height={800}
          />
        </div>
      }
      {
        postBody &&
        <h2 className="PostBody_title GoldText pb-0">{get(blogpost, 'title')}</h2>
      }
      {
        postDate &&
        <div className="PostBody_date">
          {moment(postDate).format('MMMM D, YYYY')}
        </div>
      }
      {
        postBody &&
        <div className="PostBody_body" dangerouslySetInnerHTML={SanitizeHtml(postBody, sanitizeHtmlOptions)} />
      }
    </article>
  );
}

PostBody.propTypes = {
  blogpost: PropTypes.object.isRequired,
  className: PropTypes.string,
};

PostBody.defaultProps = {
  className: '',
};

export default PostBody;



// WEBPACK FOOTER //
// ./src/pages/BlogpostDetail/PostBody.js